import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowRightLeft, BriefcaseBusiness, CalendarCheck, FileSignature, FileText, Handshake, IndianRupee, KeyRound,
  LayoutDashboard, Link2, Mail, MoreHorizontal, Pencil, Plus, Trash2, TrendingUp, UserRound, UserX,
} from "lucide-react";
import {
  ActionMenu, ActionMenuDivider, ActionMenuItem, AlertModal, AppButton, Callout, ConfirmModal,
  EmptyState, PageHeader, StatsCard, StatsGrid, Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import CommonToaster from "../../Common/CommonToaster";
import TransferBranchModal from "../../Common/TransferBranchModal";
import CallStatusToast from "../../Telephony/CallStatusToast";
import apiClient from "../../../utils/apiClient";
import { hasPermission } from "../../../utils/permissions";
import { can as allowed } from "../../../utils/access";
import { usePlan } from "../../../contexts/PlanContext";
import { useBranch } from "../../../contexts/BranchContext";
import { useSocket } from "../../../contexts/SocketContext";
import useAudienceTerm from "../../../hooks/useAudienceTerm";
import useClickToCall from "../../../hooks/useClickToCall";
import useStudentRecord from "./useStudentRecord";
import StudentIdentityCard from "./StudentIdentityCard";
import StudentAttention from "./StudentAttention";
import StudentVitals from "./StudentVitals";
import OverviewTab from "./tabs/OverviewTab";
import ProfileTab from "./tabs/ProfileTab";
import AttendanceTab from "./tabs/AttendanceTab";
import PerformanceTab from "./tabs/PerformanceTab";
import FeesTab from "./tabs/FeesTab";
import PlacementTab from "./tabs/PlacementTab";
import PortfolioTab from "./tabs/PortfolioTab";
import DocumentsTab from "./tabs/DocumentsTab";
import EvaluationTab from "./tabs/EvaluationTab";
import useStudentDocuments from "../profile/useStudentDocuments";
import { staffDocumentsApi } from "../profile/documentsApi";
import {
  accountState, attendanceStanding, buildAttention, engagement, listEnrollments, liveEnrollment,
  summarizeFees, summarizeInstallments,
} from "./studentRecord";

// `needs` names what a tab depends on: the fee plan feature, or one of the two
// student-profile module switches (Settings → Modules).
const TABS = [
  { value: "overview", label: "Overview", icon: LayoutDashboard },
  { value: "profile", label: "Profile", icon: UserRound },
  { value: "attendance", label: "Attendance", icon: CalendarCheck },
  { value: "performance", label: "Performance", icon: TrendingUp },
  { value: "fees", label: "Fees", icon: IndianRupee, needs: "fees" },
  { value: "placement", label: "Placement", icon: Handshake },
  { value: "portfolio", label: "Portfolio", icon: BriefcaseBusiness },
  { value: "documents", label: "Documents", icon: FileText, needs: "advancedProfile" },
  { value: "evaluation", label: "Evaluation", icon: FileSignature, needs: "contractStudents" },
];

/** The skeleton mirrors the page, so nothing jumps when the record lands. */
const RecordSkeleton = () => (
  <div className="tw-flex tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading student">
    <Shimmer height={132} />
    <StatsGrid cols={4}>
      {[0, 1, 2, 3].map((i) => <StatsCard key={i} loading />)}
    </StatsGrid>
    <Shimmer height={40} />
    <div className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-3">
      <div className="min-[1200px]:tw-col-span-2"><Shimmer height={280} /></div>
      <Shimmer height={280} />
    </div>
  </div>
);

/**
 * StudentProfilePage — `/students/:id`, the one place an admin reads everything
 * about a student and acts on it.
 *
 * Reading order, top to bottom: who they are and how to reach them; what needs
 * attention (only when something does); four vitals; then a tab per question —
 * Overview, Profile, Attendance, Performance, Fees, Placement, Portfolio. The
 * tab is in the URL (`?tab=fees`), so a link can land on it and a refresh keeps it.
 *
 * Permissions are decided HERE and nowhere below: every child receives either a
 * handler or nothing, and renders the action only when it got one. The API
 * enforces the same rules; this is what keeps a button from promising what the
 * server will refuse.
 */
const StudentProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const terms = useAudienceTerm();
  const plan = usePlan();
  const { hasPlanFeature } = plan;
  const hasStudentModule = plan.hasStudentModule || (() => false);
  const { isMultiBranch, branches } = useBranch();
  const { socket } = useSocket();

  const feesEnabled = hasPlanFeature("feeManagement") && hasPermission("view_fees");
  const modules = {
    advancedProfile: hasStudentModule("advancedProfile"),
    contractStudents: hasStudentModule("contractStudents"),
  };
  const features = {
    interviews: hasPlanFeature("interviews"),
    aiMock: hasPlanFeature("aiMock"),
    aiLearning: hasPlanFeature("aiLearning"),
    codingPractice: hasPlanFeature("codingPractice"),
    quizAssessment: hasPlanFeature("quizAssessment"),
  };
  const can = {
    edit: hasPermission("add_edit_students"),
    delete: hasPermission("delete_students"),
    collect: feesEnabled && allowed("fees.collect"),
    remind: feesEnabled && allowed("fees.invoice"),
    // The transfer endpoint requires `manage_branches`; offering it to anyone
    // else is a button that always fails.
    changeBranch: isMultiBranch && branches.length > 1 && hasPermission("manage_branches"),
    addInterview: features.interviews && hasPermission("manage_interviews"),
    // Documents and work history are the student editor's; verifying them and
    // writing the evaluation is the same right (vc_be accessPolicy); signing for
    // the institute is its own.
    evaluate: modules.contractStudents && allowed("students.evaluate"),
    sign: modules.contractStudents && allowed("agreements.sign"),
  };

  const { record, stats, plans, payments, portfolio, interviews } = useStudentRecord(id, {
    feesEnabled,
    interviewsEnabled: features.interviews,
  });
  const documentsApi = useMemo(() => staffDocumentsApi(id), [id]);
  const docs = useStudentDocuments(documentsApi, modules.advancedProfile);
  const call = useClickToCall();

  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const notify = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [reminding, setReminding] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);

  // A class starting or ending anywhere reaches every admin; keep the "live"
  // badges on this record honest without refetching it.
  const [liveOverrides, setLiveOverrides] = useState({});
  useEffect(() => {
    if (!socket) return undefined;
    const onNotification = (data) => {
      if (!data?.batchId || (data.type !== "class_started" && data.type !== "class_ended")) return;
      setLiveOverrides((prev) => ({ ...prev, [String(data.batchId)]: data.type === "class_started" }));
    };
    socket.on("notification", onNotification);
    return () => socket.off("notification", onNotification);
  }, [socket]);

  const student = record.data?.student;
  const enrollments = useMemo(() => listEnrollments(student, record.data?.courseAssignments).map((e) => {
    const batchId = e.batch?._id && String(e.batch._id);
    return batchId && batchId in liveOverrides
      ? { ...e, batch: { ...e.batch, isLive: liveOverrides[batchId] } }
      : e;
  }), [student, record.data, liveOverrides]);

  const requestedTab = searchParams.get("tab");
  const visibleTabs = TABS.filter((t) => {
    if (!t.needs) return true;
    if (t.needs === "fees") return feesEnabled;
    return !!modules[t.needs];
  });
  const tab = visibleTabs.some((t) => t.value === requestedTab) ? requestedTab : "overview";
  const setTab = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === "overview") params.delete("tab");
      else params.set("tab", next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/students"));
  const pageHeader = <PageHeader title={`${terms.singular} profile`} icon={UserRound} backButton onBack={goBack} />;

  if (record.loading && !student) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        <RecordSkeleton />
      </div>
    );
  }

  if (!student) {
    const notFound = record.error?.response?.status === 404;
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        {pageHeader}
        {notFound ? (
          <EmptyState
            icon={UserX}
            title={`${terms.singular} not found`}
            description="They may have been deleted, or the link belongs to another institute."
            action={<AppButton variant="outline" onClick={() => navigate("/students")}>Back to {terms.lowerPlural}</AppButton>}
          />
        ) : (
          <Callout
            tone="danger"
            role="alert"
            action={<AppButton size="xs" variant="outline" onClick={record.reload}>Try again</AppButton>}
          >
            {record.error?.response?.data?.message || `Could not load this ${terms.lower}.`}
          </Callout>
        )}
      </div>
    );
  }

  /* ── Derived ─────────────────────────────────────────────────────────────── */

  const primary = enrollments.find((e) => e.isPrimary) || enrollments[0] || null;
  const otherCourses = enrollments.filter((e) => e !== primary && e.status === "active").length;
  const live = liveEnrollment(enrollments);
  const fees = feesEnabled ? summarizeFees(enrollments) : null;
  const installments = feesEnabled ? summarizeInstallments(plans.data || []) : null;
  const attendance = attendanceStanding(stats.data, student);
  const activity = engagement(student);
  const login = accountState(student);
  const attention = buildAttention({ student, installments, attendance, activity, feesEnabled });

  /* ── Actions ─────────────────────────────────────────────────────────────── */

  const goCollect = () => navigate(`/students/${id}/fees/new`);
  const goEdit = () => navigate(`/students/edit/${id}`);
  const addInterview = () => navigate("/interviews/new", {
    state: { student: { _id: student._id, name: student.name, email: student.email, mobile: student.mobile } },
  });
  const reloadMoney = () => {
    record.reload();
    plans.reload();
    payments.reload();
  };
  const placeCall = () => call.placeCall({ studentId: student._id });

  const remind = async ({ planId, seq, label }) => {
    setReminding(`${planId}:${seq}`);
    try {
      const { data } = await apiClient.post(`/fees/plans/${planId}/installments/${seq}/remind`);
      const channels = [data?.emailSent && "email", data?.whatsappQueued && "WhatsApp"].filter(Boolean);
      if (channels.length) notify(`${label} reminder sent by ${channels.join(" and ")}.`);
      else notify("No reminder went out — check the student's email and the WhatsApp reminder settings.", "warning");
    } catch (err) {
      notify(err?.response?.data?.message || "Could not send the reminder.", "error");
    }
    setReminding("");
  };

  const confirmThen = (spec, run) => setConfirm({
    ...spec,
    onConfirm: async () => {
      setConfirmBusy(true);
      try {
        await run();
      } finally {
        setConfirmBusy(false);
        setConfirm(null);
      }
    },
  });

  const resendWelcome = () => confirmThen({
    title: "Resend welcome email?",
    message: `${student.name} will get the sign-up link again at ${student.email}.`,
    confirmText: "Resend",
    variant: "info",
  }, async () => {
    try {
      await apiClient.post(`/students/${id}/resend-welcome-email`);
      notify(`Welcome email sent to ${student.email}.`);
    } catch (err) {
      notify(err?.response?.data?.message || "Could not send the welcome email.", "error");
    }
  });

  const resetPassword = () => confirmThen({
    title: "Reset password?",
    message: `${student.name}'s current password stops working straight away. You will get a temporary one to share with them.`,
    confirmText: "Reset password",
    variant: "warning",
  }, async () => {
    try {
      const { data } = await apiClient.put(`/users/${student.account.userId}/reset-password`);
      setTempPassword({ password: data.tempPassword, emailed: data.emailed });
    } catch (err) {
      notify(err?.response?.data?.message || "Could not reset the password.", "error");
    }
  });

  // The emailed no-login link into the student's own profile form — education,
  // companies, documents and, as a contract student, the agreement to sign.
  const sendProfileLink = () => confirmThen({
    title: modules.contractStudents ? "Send evaluation link?" : "Send profile link?",
    message: `${student.name} will get an email at ${student.email} with a private link to fill in their education, previous companies and documents${modules.contractStudents ? ", submit for review and later sign the agreement" : ""} — no login needed. The link lasts 60 days.`,
    confirmText: "Send link",
    variant: "info",
  }, async () => {
    try {
      const { data } = await apiClient.post(`/student-profile/students/${id}/link`);
      notify(`Link emailed to ${data.sentTo}.`);
    } catch (err) {
      notify(err?.response?.data?.message || "Could not send the link.", "error");
    }
  });

  const deleteStudent = () => confirmThen({
    title: `Delete ${student.name}?`,
    message: "This removes their record, their login and their course enrolments. It cannot be undone.",
    confirmText: `Delete ${terms.lower}`,
    variant: "danger",
  }, async () => {
    try {
      await apiClient.delete(`/students/${id}`);
      navigate("/students", { replace: true });
    } catch (err) {
      notify(err?.response?.data?.message || `Could not delete this ${terms.lower}.`, "error");
    }
  });

  const canResetPassword = can.edit && login === "active" && !!student.account?.userId;
  const canResendWelcome = can.edit && login === "invited";
  const canSendLink = modules.advancedProfile && can.edit && !!student.email;

  const menuItems = [
    canSendLink && (
      <ActionMenuItem key="profile-link" onClick={sendProfileLink}>
        <Link2 size={14} /> {modules.contractStudents ? "Send evaluation link" : "Send profile link"}
      </ActionMenuItem>
    ),
    can.changeBranch && (
      <ActionMenuItem key="branch" onClick={() => setTransferOpen(true)}>
        <ArrowRightLeft size={14} /> Change branch
      </ActionMenuItem>
    ),
    canResetPassword && (
      <ActionMenuItem key="reset" onClick={resetPassword}><KeyRound size={14} /> Reset password</ActionMenuItem>
    ),
    canResendWelcome && (
      <ActionMenuItem key="welcome" onClick={resendWelcome}><Mail size={14} /> Resend welcome email</ActionMenuItem>
    ),
    can.addInterview && (
      <ActionMenuItem key="interview" onClick={addInterview}><Plus size={14} /> Add interview</ActionMenuItem>
    ),
  ].filter(Boolean);
  if (can.delete) {
    if (menuItems.length) menuItems.push(<ActionMenuDivider key="divider" />);
    menuItems.push(
      <ActionMenuItem key="delete" danger onClick={deleteStudent}>
        <Trash2 size={14} /> Delete {terms.lower}
      </ActionMenuItem>,
    );
  }

  const headerActions = (
    <>
      {can.collect && (
        <AppButton size="sm" onClick={goCollect}><IndianRupee size={16} /> Collect fee</AppButton>
      )}
      {can.edit && (
        <AppButton size="sm" variant={can.collect ? "outline" : "default"} onClick={goEdit}>
          <Pencil size={16} /> Edit
        </AppButton>
      )}
      {menuItems.length > 0 && (
        <ActionMenu label={`More actions for ${student.name}`} trigger={<MoreHorizontal size={16} />}>
          {menuItems}
        </ActionMenu>
      )}
    </>
  );

  const overdue = installments?.oldestOverdue;
  const attentionActions = {
    remind: overdue && can.remind ? {
      label: "Send reminder",
      busyLabel: "Sending…",
      busy: reminding === `${overdue.planId}:${overdue.seq}`,
      onClick: () => remind(overdue),
    } : undefined,
    attendance: { label: "View attendance", onClick: () => setTab("attendance") },
    resendWelcome: canResendWelcome ? { label: "Resend welcome email", onClick: resendWelcome } : undefined,
    edit: can.edit ? { label: "Add email", onClick: goEdit } : undefined,
    call: student.mobile && call.enabled
      ? { label: "Call", busyLabel: "Calling…", busy: call.calling, onClick: placeCall }
      : undefined,
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {pageHeader}

      <StudentIdentityCard
        student={student}
        primary={primary}
        otherCourses={otherCourses}
        accountState={login}
        activity={activity}
        live={live}
        onCall={student.mobile && call.enabled ? placeCall : undefined}
        calling={call.calling}
        actions={headerActions}
      />

      <StudentAttention items={attention} actions={attentionActions} />

      <StudentVitals
        attendance={attendance}
        stats={stats}
        fees={fees}
        installments={installments}
        activity={activity}
        hasBatch={!!primary?.batch}
      />

      <Tabs value={tab} onChange={setTab}>
        <TabsList overflow>
          {visibleTabs.map(({ value, label, icon: Icon }) => (
            <TabsTrigger key={value} value={value}>
              <Icon size={15} className="tw-mr-1" aria-hidden="true" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            student={student}
            enrollments={enrollments}
            stats={stats}
            fees={fees}
            installments={installments}
            payments={payments}
            portfolio={portfolio}
            interviews={interviews}
            features={features}
            onOpenTab={setTab}
            onEdit={can.edit ? goEdit : undefined}
          />
        </TabsContent>

        <TabsContent value="profile">
          <ProfileTab
            student={student}
            accountState={login}
            activity={activity}
            onEdit={can.edit ? goEdit : undefined}
            onResetPassword={canResetPassword ? resetPassword : undefined}
            onResendWelcome={canResendWelcome ? resendWelcome : undefined}
          />
        </TabsContent>

        <TabsContent value="attendance">
          <AttendanceTab
            studentId={student._id}
            stats={stats}
            attendance={attendance}
            batchName={student.batch?.name}
          />
        </TabsContent>

        <TabsContent value="performance">
          <PerformanceTab stats={stats} portfolio={portfolio} features={features} />
        </TabsContent>

        {feesEnabled && (
          <TabsContent value="fees">
            <FeesTab
              student={student}
              enrollments={enrollments}
              fees={fees}
              installments={installments}
              plans={plans}
              payments={payments}
              canCollect={can.collect}
              onCollect={goCollect}
              onRemind={can.remind ? remind : undefined}
              reminding={reminding}
              onChanged={reloadMoney}
              onNotify={notify}
            />
          </TabsContent>
        )}

        <TabsContent value="placement">
          <PlacementTab
            student={student}
            stats={stats}
            portfolio={portfolio}
            interviews={interviews}
            features={features}
            onAddInterview={can.addInterview ? addInterview : undefined}
          />
        </TabsContent>

        <TabsContent value="portfolio">
          <PortfolioTab studentId={student._id} portfolio={portfolio} />
        </TabsContent>

        {modules.advancedProfile && (
          <TabsContent value="documents">
            <DocumentsTab student={student} docs={docs} canEdit={can.edit} canVerify={can.evaluate} />
          </TabsContent>
        )}

        {modules.contractStudents && (
          <TabsContent value="evaluation">
            <EvaluationTab
              studentId={student._id}
              student={student}
              docs={docs}
              canEvaluate={can.evaluate}
              canSign={can.sign}
              onOpenTab={setTab}
              onNotify={notify}
              onSendLink={canSendLink ? sendProfileLink : undefined}
            />
          </TabsContent>
        )}
      </Tabs>

      <ConfirmModal
        show={!!confirm}
        onHide={() => { if (!confirmBusy) setConfirm(null); }}
        onConfirm={() => confirm?.onConfirm()}
        title={confirm?.title || ""}
        message={confirm?.message || ""}
        confirmText={confirm?.confirmText || "Confirm"}
        variant={confirm?.variant || "danger"}
        loading={confirmBusy}
      />

      <AlertModal
        show={!!tempPassword}
        onClose={() => setTempPassword(null)}
        title="Temporary password"
        variant="success"
        buttonText="Done"
        message={tempPassword && (
          <span className="tw-flex tw-flex-col tw-gap-2">
            <span>
              {tempPassword.emailed
                ? `It has also been emailed to ${student.email}.`
                : "There is no email on file, so share it with them directly."}
              {" "}Ask them to change it after signing in.
            </span>
            <code className="tw-self-start tw-rounded-sm tw-bg-surface-sunken tw-px-2.5 tw-py-1 tw-font-mono tw-text-sm tw-text-content">
              {tempPassword.password}
            </code>
          </span>
        )}
      />

      <TransferBranchModal
        show={transferOpen}
        onHide={() => setTransferOpen(false)}
        selectedIds={[id]}
        mode="students"
        onTransfer={() => {
          setTransferOpen(false);
          record.reload();
          notify(`${student.name} moved to the new branch.`);
        }}
      />

      <CallStatusToast call={call.activeCall} error={call.error} onDismiss={call.dismiss} />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/** The old standalone `/students/:id/portfolio` link, kept working. */
export const StudentPortfolioRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/students/${id}?tab=portfolio`} replace />;
};

export default StudentProfilePage;
